import axios from 'axios';


// production api_url
const API_URL = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8000/api/';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for session-based authentication during impersonation
});

const buildDateRangeQuery = (days, startDate, endDate) => {
  const params = new URLSearchParams();
  if (days) {
    params.set('days', days);
  }
  if (startDate) {
    params.set('start_date', startDate);
  }
  if (endDate) {
    params.set('end_date', endDate);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

// Appends the global dashboard filters ("Prospects only" / "Unread only") to a
// query string built by buildDateRangeQuery.
const appendGlobalFilters = (query, leadCategory, unreadOnly) => {
  let result = query;
  const append = (pair) => {
    result += `${result ? '&' : '?'}${pair}`;
  };
  if (leadCategory) {
    append(`lead_category=${leadCategory}`);
  }
  if (unreadOnly) {
    append('unread_only=true');
  }
  return result;
};

// Access tokens last 60 minutes (SIMPLE_JWT in settings.py). When one expires, trade the
// refresh token for a new one and retry, instead of sending staff to the login screen
// every hour. Only one refresh runs at a time: the backend blacklists a refresh token the
// first time it is used, so a second concurrent refresh would fail.
let refreshRequest = null;

const refreshAccessToken = () => {
  if (!refreshRequest) {
    const refresh = localStorage.getItem('refresh_token');
    // Plain axios, not `api`: a failed refresh must not re-enter the interceptor below.
    refreshRequest = axios
      .post(`${API_URL}token/refresh/`, refresh ? { refresh } : {}, { withCredentials: true })
      .then(({ data }) => {
        localStorage.setItem('access_token', data.access);
        if (data.refresh) {
          localStorage.setItem('refresh_token', data.refresh);
        }
        return data.access;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }
  return refreshRequest;
};

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    const isImpersonating = localStorage.getItem('authToken') === 'impersonation_session';
    if (error.response?.status === 401 && original && !original._retried && !isImpersonating) {
      original._retried = true;
      const tokenBefore = localStorage.getItem('access_token');
      try {
        await refreshAccessToken();
        // The request interceptor attaches the new access token.
        return api(original);
      } catch (refreshError) {
        // Another tab may have refreshed at the same moment and used up the shared
        // refresh token. If that tab stored a new access token, retry with it.
        const latest = localStorage.getItem('access_token');
        if (latest && latest !== tokenBefore) {
          return api(original);
        }
      }
    }
    if (error.response?.status === 401) {
      // Check if we're in an impersonation session before clearing tokens
      try {
        // Use backend URL based on environment
        const isDevelopment = window.location.hostname === 'localhost';
        const baseUrl = isDevelopment ? 'http://localhost:8000' : '';
        
        const impersonationResponse = await fetch(`${baseUrl}/api/impersonation/status/`, {
          credentials: 'include'
        });
        
        if (impersonationResponse.ok) {
          const impersonationData = await impersonationResponse.json();
          
          if (impersonationData.impersonating) {
            // We're in impersonation, don't clear tokens or redirect
            console.log('API 401 during impersonation - this is expected');
            return Promise.reject(error);
          }
        }
      } catch (impersonationError) {
        console.log('Not in impersonation session');
      }
      
      // Clear all authentication data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('locationInfo');
      
      // Dispatch a custom event to notify React components
      window.dispatchEvent(new CustomEvent('auth:logout'));
      
      window.location.href = '/login';
    }
    return Promise.reject(error)
  }
);

api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  const authToken = localStorage.getItem('authToken');
  
  // If we're in impersonation mode, don't add JWT authorization
  if (authToken === 'impersonation_session') {
    // Session-based auth during impersonation
    return config;
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Company Methods
export const getCompanies = async (authToken) => {
  const response = await api.get(`${API_URL}companies/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};
export const getCompany = async (authToken, id) => {
  const response = await api.get(`${API_URL}companies/${id}/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

export const updatePassword = async (authToken, formData) => {
  try {
  const response = await api.post(`${API_URL}password/update/`, formData, {
    headers: { Authorization: `Token ${authToken}` },
  });
  return response.data;
  } catch (error) {
    console.error('API error:', error.response); // Log error for debugging
    throw error; // Rethrow to handle in component
  }
};

// Leads Methods
export const getLeadSources = async (authToken) => {
  const response = await api.get(`${API_URL}lead-sources/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

export const getLeadMessages = async (authToken, lead_id) => {
  const response = await api.get(`${API_URL}leads/${lead_id}/messages/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

export const sendLeadMessage = async (authToken, leadId, messageType, content) => {
  const response = await api.post(
    `${API_URL}leads/${leadId}/messages/send/`,
    { message_type: messageType, content },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};

export const markLeadMessagesAsRead = async (leadId) => {
  // Auth is supplied by the request interceptor (Bearer access_token), same as
  // markLeadAsRead. Don't pass an explicit header from a prop — that prop can be
  // stale/empty and was causing this write to be skipped.
  const response = await api.post(
    `${API_URL}leads/${leadId}/messages/mark_all_read/`,
    {},
  );
  return response.data;
};

// Message Template Methods
export const getMessageTemplates = async () => {
  const response = await api.get(`${API_URL}message-templates/`);
  return response.data;
};

export const createMessageTemplate = async (authToken, templateData) => {
  const response = await api.post(
    `${API_URL}message-templates/`,
    templateData,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};

export const updateMessageTemplate = async (authToken, templateId, templateData) => {
  const response = await api.put(
    `${API_URL}message-templates/${templateId}/`,
    templateData,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};

export const deleteMessageTemplate = async (authToken, templateId) => {
  const response = await api.delete(
    `${API_URL}message-templates/${templateId}/`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};

// Automatic missed-call texts (company admin writes, all staff read)
export const getMissedCallMessages = async () => {
  const response = await api.get(`${API_URL}missed-call-messages/`);
  return response.data;
};

export const saveMissedCallMessage = async (templateType, content) => {
  const response = await api.put(`${API_URL}missed-call-messages/`, { [templateType]: content });
  return response.data;
};

export const resetMissedCallMessage = async (templateType) => {
  const response = await api.delete(`${API_URL}missed-call-messages/`, {
    params: { template_type: templateType },
  });
  return response.data;
};

export const getLeadStatusCounts = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/status_counts/${query}`
  );
  return response.data;
};

export const getLeadsByCategory = async (days, startDate, endDate, unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), '', unreadOnly);
  const response = await api.get(
    `${API_URL}leads/category_counts/${query}`
  );
  return response.data; // Returns [{name: "prospect", value: 42}, ...]
};

export const getUnreadMessagesCount = async () => {
  const response = await api.get(`${API_URL}leads/unread_messages_count/`);
  return response.data.count;
};

// Lead Read Status Methods
export const getUnreadLeadsCount = async (leadCategory = '') => {
  const cat = leadCategory ? `?lead_category=${leadCategory}` : '';
  const response = await api.get(`${API_URL}leads/unread_leads_count/${cat}`);
  return response.data.count;
};

export const getNeedsCallbackCount = async (leadCategory = '') => {
  const cat = leadCategory ? `?lead_category=${leadCategory}` : '';
  const response = await api.get(`${API_URL}leads/needs_callback_count/${cat}`);
  return response.data.count;
};

export const markLeadAsRead = async (leadId) => {
  const response = await api.post(`${API_URL}leads/${leadId}/mark_read/`);
  return response.data;
};

export const markLeadAsUnread = async (leadId) => {
  const response = await api.post(`${API_URL}leads/${leadId}/mark_unread/`);
  return response.data;
};

// Clears a waiting reply without texting the patient. Returns the updated lead.
export const markNoReplyNeeded = async (leadId) => {
  const response = await api.post(`${API_URL}leads/${leadId}/no_reply_needed/`);
  return response.data;
};

export const getLeadsWithUnreadMessages = async (leadCategory = '', unreadOnly = false) => {
  const cat = leadCategory ? `&lead_category=${leadCategory}` : '';
  const unread = unreadOnly ? '&unread_only=true' : '';
  const response = await api.get(`${API_URL}leads/?unread_messages=true${cat}${unread}`);
  return response.data;
};

// Leads whose latest message is a patient text no staff has answered. Same
// parameters as the leads list "waiting_reply" filter, so the number shown
// here is the number the list shows after the click.
export const getWaitingReplies = async ({
  days = '',
  startDate = '',
  endDate = '',
  leadCategory = '',
  unreadOnly = false,
  pageSize = 3,
} = {}) => {
  const params = new URLSearchParams({ waiting_reply: 'true', page_size: pageSize });
  if (days) params.set('days', days);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (leadCategory) params.set('lead_category', leadCategory);
  if (unreadOnly) params.set('is_read_by_user', 'false');
  const response = await api.get(`${API_URL}leads/?${params.toString()}`);
  return response.data; // Returns {count, results: [...]}
};

export const getLeadsByType = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/type_counts/${query}`
  );
  return response.data; // Returns [{name: "website", value: 5}, ...]
};

export const getLeadsBySource = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/source_counts/${query}`
  );
  return response.data; // Returns [{name: "google", value: 5}, ...]
};

export const getLeadPriorityCounts = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/priority_counts/${query}`
  );
  return response.data; // Returns {high_priority: 12, standard: 25, routine: 10, urgent: 3, no_analysis: 150}
};

export const getLeadFollowUpCounts = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/follow_up_counts/${query}`
  );
  return response.data; // Returns {past_due: N, due_today: N, due_this_week: N, due_later: N, no_follow_up: N}
};

export const getLeadsByLossReason = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/loss_reason_counts/${query}`
  );
  return response.data; // Returns {total_lost_leads: 10, chart_data: [{name: "insurance_not_accepted", value: 3, label: "Insurance Not Accepted"}, ...]}
};


// --- Dashboard AI metrics ------------------------------------------------

// The worklist. One call returns every reason a lead needs a person; each count
// comes from the same filter the Leads-page link behind it uses.
export const getNeedsAttention = async (leadCategory = '', unreadOnly = false) => {
  // Takes no date range: "needs attention" means open right now.
  const query = appendGlobalFilters('', leadCategory, unreadOnly);
  const response = await api.get(`${API_URL}leads/needs_attention/${query}`);
  // Returns {total, reasons: {waiting_reply: {count, lead_id}, past_due, due_today, unopened: {.., over_24h}, hot_new,
  //   appointments_today: {.., date}, appointments_tomorrow}, next_actions: {call_back: {count, lead_id}, ...}}
  return response.data;
};

// New activity after the `since` cursor (null = first poll: no events, just a cursor).
// Returns {events: [{kind: 'new_lead'|'inbound_text'|'repeat_submission'|'call', lead_id, lead_name, at}], since}
export const getLeadActivity = async (since = null) => {
  const query = since ? `?since=${encodeURIComponent(since)}` : '';
  const response = await api.get(`${API_URL}leads/activity/${query}`);
  return response.data;
};

export const getConcernCounts = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/concern_counts/${query}`
  );
  return response.data; // Returns [{name: "insurance", label: "Insurance coverage", value: 6}, ...] — descending, excludes "none"
};

export const getServiceInterestCounts = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/service_interest_counts/${query}`
  );
  return response.data; // Returns [{name: "Implants", label: "Implants", value: 5, estimated_value: 12500}, ...] — top 8
};

export const getLeadsByDate = async (days, startDate, endDate, leadCategory = '', unreadOnly = false) => {
  const query = appendGlobalFilters(buildDateRangeQuery(days, startDate, endDate), leadCategory, unreadOnly);
  const response = await api.get(
    `${API_URL}leads/date_counts/${query}`
  );
  return response.data; // Returns [{date: "Aug 1", total: 5, status_new: 2, status_contacted: 3}, ...]
};

export const getLeads = async (
  status = '',
  type = '',
  source = '',
  sortOrder = '-created_at',
  page = 1,
  pageSize = 15,
  followUpStatus = '',
  selectedLastSeen = '',
  unreadMessages = false,
  isReadFilter = '',
  searchQuery = '',
  appointmentDate = '',
  aiScore = '',
  aiIsUrgent = false,
  isReadByUser = '',
  activityDays = '',
  activityStartDate = '',
  activityEndDate = '',
  leadCategory = '',
  needsCallback = false,
  nextAction = '',
  untouched = false,
  waitingReply = false,
  lossReason = ''
) => {
    const queryParams = new URLSearchParams({
      status,
      type,
      source,
      ...(needsCallback && { needs_callback: 'true' }),
      ...(waitingReply && { waiting_reply: 'true' }),
      ...(untouched && { untouched: 'true' }),
      ...(followUpStatus && { follow_up_status: followUpStatus }),
      ...(selectedLastSeen && { last_seen: selectedLastSeen }),
      ...(unreadMessages && { unread_messages: unreadMessages }),
      ...(isReadFilter && { is_read: isReadFilter }),
      ...(isReadByUser !== '' && { is_read_by_user: isReadByUser }),
      ...(searchQuery && { search: searchQuery }),
      ...(appointmentDate && { appointment_date: appointmentDate }),
      ...(aiScore && { ai_score: aiScore }),
      ...(aiIsUrgent && { ai_is_urgent: aiIsUrgent }),
      ...(activityDays && { days: activityDays }),
      ...(activityStartDate && { start_date: activityStartDate }),
      ...(activityEndDate && { end_date: activityEndDate }),
      ...(leadCategory && { lead_category: leadCategory }),
      ...(nextAction && { next_action: nextAction }),
      ...(lossReason && { loss_reason: lossReason }),
      ordering: sortOrder,
      page,
      page_size: pageSize,
    });

    const response = await api.get(`${API_URL}leads/?${queryParams.toString()}&page_size=${pageSize}`);
    return response.data;
  };

export const getLead = async (authToken, id) => {
  console.log("LeadData:", authToken);
  console.log("LeadData:", id);
  // const response = await api.get(`${API_URL}leads/${id}/`);
  const response = await api.get(`${API_URL}leads/${id}/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  console.log("response:", response);
  return response.data;
};

export const getInfo = async () => {
  const response = await api.get(`${API_URL}info/`);
  console.log("response:", response);
  return response.data;
};

// Feature announcements this user has seen, as { announcement_id: seen_at }
export const getSeenAnnouncements = async () => {
  const response = await api.get(`${API_URL}announcements/seen/`);
  return response.data;
};

// Record announcements as seen. Returns the updated { announcement_id: seen_at } map.
export const markAnnouncementsSeen = async (announcementIds) => {
  const response = await api.post(`${API_URL}announcements/seen/`, {
    announcement_ids: announcementIds,
  });
  return response.data;
};

export const getNotificationSettings = async () => {
  const response = await api.get(`${API_URL}notification-settings/`);
  return response.data;
};

export const updateNotificationSettings = async (settings) => {
  const response = await api.put(`${API_URL}notification-settings/`, settings);
  return response.data;
};

export const createLead = async (authToken, leadData) => {
  console.log("LeadData:", leadData);
  console.log("AuthToken:", authToken);
  const response = await api.post(`${API_URL}leads/`, leadData, {
    headers: { Authorization: `Token ${authToken}` }
  });
  console.log("response:", response);
  return response.data;
};

export const editLead = async (authToken, leadId, leadData) => {
  console.log("api_url:", API_URL);
  console.log("LeadData:", leadData);
  console.log("LeadId:", leadId);
  const response = await api.put(`${API_URL}leads/${leadId}/`, leadData);
  return response.data;
}

export const updateLead = async (authToken, leadId, leadData) => {
  console.log("api_url:", API_URL);
  console.log("LeadData:", leadData);
  console.log("LeadId:", leadId);
  const response = await api.patch(`${API_URL}leads/${leadId}/`, leadData);
  return response.data;
}

export const deleteLead = async (authToken, leadId) => {
  try {
    const response = await api.delete(`${API_URL}leads/${leadId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Patient Methods
export const getPatients = async (authToken) => {
  const response = await api.get(`${API_URL}patients/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

export const getPatient = async (authToken, id) => {
  console.log("PatientData:", authToken);
  console.log("PatientData:", id);
  const response = await api.get(`${API_URL}patients/${id}/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  console.log("response:", response);
  return response.data;
};

export const createPatient = async (authToken, patientData) => {
  console.log("PatientData:", patientData);
  console.log("PatientData:", authToken);
  const response = await api.post(`${API_URL}patients/`, patientData, {
    headers: { Authorization: `Token ${authToken}` }
  });
  console.log("response:", response);
  return response.data;
};

export const editPatient = async (patientData, id, authToken) => {
  const response = await api.put(`${API_URL}patients/${id}/`, patientData, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

// Opportunity methods
export const getOpportunities= async (authToken) => {
  const response = await api.get(`${API_URL}opportunities/`, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

// Location methods
export const getLocations = async () => {
  const response = await api.get(`${API_URL}locations/`);
  console.log('Locations API response:', response.data);
  console.log('Full API response:', response);
    console.log('Response data type:', typeof response.data);
    console.log('Response data:', response.data);
  return response.data.results;
};

export const updateLocation = async (locationId, data) => {
  const response = await api.patch(`${API_URL}locations/${locationId}/`, data);
  return response.data;
};

export const updateCompany = async (companyId, data) => {
  const response = await api.patch(`${API_URL}companies/${companyId}/`, data);
  return response.data;
};

// Account methods
export const getAccounts= async (authToken) => {
  const response = await api.get(`${API_URL}accounts/`);
  return response.data;
};
// Lead tasks
export const getLeadTasks = async (id) => {
  if (!id) {
    throw new Error('Lead ID is required');
  }
  try {
    const response = await api.get(`${API_URL}leads/${id}/tasks/`);
    return response.data.results;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const getPatientTasks = async (authToken, id) => {
  if (!id) {
    throw new Error('Patient ID is required');
  }
  try {
    const response = await api.get(`${API_URL}patients/${id}/tasks/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const getLeadNotes = async (id) => {
  if (!id) {
    throw new Error('Lead ID is required');
  }
  try {
    const response = await api.get(`${API_URL}leads/${id}/notes/`);
    return response.data.results;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const getPatientNotes = async (authToken, id) => {
  if (!id) {
    throw new Error('Patient ID is required');
  }
  try {
    const response = await api.get(`${API_URL}patients/${id}/notes/`)
    return response.data;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const getNote = async (authToken, leadId, noteId) => {
  console.log("noteId: ", noteId)
  console.log("authToken: ", authToken)
  if (!noteId) {
    throw new Error('Note ID is for sure required');
  }
  try {
    const response = await api.get(`${API_URL}leads/${leadId}/notes/${noteId}/`, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
    return response.data;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const createNote = async (authToken, contentType, patientId, noteData) => {
  console.log("authToken:", authToken);
  console.log("patientId:", patientId);
  console.log("noteData:", noteData);
  let endpoint;
  endpoint = `${API_URL}${contentType}/${patientId}/notes/`;
  try {
    const response = await api.post(endpoint, noteData, {
      headers: { 
        Authorization: `Token ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating note:', error);
    throw error;
  }
};

export const updateNote = async (authToken, noteId, updatedData) => {
  if (!noteId) {
    throw new Error('Note ID is required');
  }
  try {
    const response = await api.put(`${API_URL}notes/${noteId}/`, updatedData, {
      headers: { 
        Authorization: `Token ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

export const deleteNote = async (authToken, noteId) => {
  if (!noteId) {
    throw new Error('Note ID is required');
  }
  try {
    await api.delete(`${API_URL}notes/${noteId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    // If successful, this will not return any data
    return true;
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

export const getTasks = async (authToken, patientId) => {
  if (!patientId) {
    throw new Error('Patient ID is required');
  }
  try {
    const response = await api.get(`${API_URL}patients/${patientId}/tasks/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

export const getTask = async (authToken, taskId) => {
  console.log("authToken: ", authToken)
  console.log("taskId: ", taskId)
  if (!taskId) {
    throw new Error('task ID is required');
  }
  try {
    const response = await api.get(`${API_URL}tasks/${taskId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

export const updateTask = async (authToken, taskId, updatedData) => {
  if (!taskId) {
    throw new Error('Task ID is required');
  }
  try {
    const response = await api.put(`${API_URL}tasks/${taskId}/`, updatedData, {
      headers: { 
        Authorization: `Token ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

export const deleteTask = async (authToken, taskId) => {
  if (!taskId) {
    throw new Error('Task ID is required');
  }
  try {
    await api.delete(`${API_URL}tasks/${taskId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    // If successful, this will not return any data
    return true;
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};




export const createTask = async (authToken, contentType, id, taskData) => {
  console.log('Task data being sent:', taskData);
  let endpoint;
  endpoint = `${API_URL}${contentType}/${id}/tasks/`;


  if (!id) {
    throw new Error('ID is required');
  }
  try {
    const response = await api.post(endpoint, taskData, {
      headers: { 
        Authorization: `Token ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
    }
    throw error;
  }
};

export const getStaffList = async (authToken) => {
  try {
    const response = await api.get(`${API_URL}available-staff/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching staff list:', error);
    throw error;
  }
};

export const getUserNotes = async (authToken) => {
  try {
    const response = await api.get(`${API_URL}notes/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user notes:', error);
    throw error;
  }
};

export const getUserTasks = async (authToken) => {
  try {
    const response = await api.get(`${API_URL}tasks/my_tasks/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user tasks:', error);
    throw error;
  }
};

export const getDocuments = async (authToken, patientId) => {
  try {
    const response = await api.get(`${API_URL}documents/?patient=${patientId}`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user documents:', error);
    throw error;
  }
};

export const getServices = async (authToken, companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }
  try {
    const response = await api.get(`${API_URL}companies/${companyId}/services/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};
export const getService = async (authToken, companyId, serviceId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }
  try {
    const response = await api.get(`${API_URL}companies/${companyId}/services/${serviceId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};
export const updateService = async (authToken, companyId, serviceId, updatedData) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }
  try {
    const response = await api.put(`${API_URL}companies/${companyId}/services/${serviceId}/`, updatedData, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};
export const createService = async (authToken, companyId, serviceData) => {
  const response = await api.post(`${API_URL}companies/${companyId}/services/`, serviceData, {
    headers: { Authorization: `Token ${authToken}` }
  });
  return response.data;
};

// Appointments
export const getAppointments = async (authToken, patientId) => {
  if (!patientId) {
    throw new Error('Patient ID is required');
  }
  try {
    const response = await api.get(`${API_URL}patients/${patientId}/appointments/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};
export const getAppointment = async (authToken, patientId, appointmentId) => {
  if (!appointmentId) {
    throw new Error('Appointment ID is required');
  }
  try {
    const response = await api.get(`${API_URL}patients/${patientId}/appointments/${appointmentId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};
export const createAppointment = async (authToken, patientId, appointmentData) => {
  try {
    const response = await api.post(`${API_URL}patients/${patientId}/appointments/`, appointmentData, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error){
    console.error("error: ", error)
  }
};
export const updateAppointment = async (authToken, patientId, id, data) => {
  if (!patientId) {
    throw new Error('Patient ID is required');
  }
  try {
    const response = await api.put(`${API_URL}patients/${patientId}/appointments/${id}/`, data, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

export const getStaffForPatient = async (authToken, patientId) => {
  const response = await api.get(`${API_URL}patients/${patientId}/available-staff/`, {
    headers: { 'Authorization': `Token ${authToken}`, }
  });
  console.log(response)
  return response.data;
};

export const getStaffForLead = async (authToken, leadId) => {
  const response = await api.get(`${API_URL}leads/${leadId}/available-staff/`, {
    headers: { 'Authorization': `Token ${authToken}`, }
  });
  console.log(response)
  return response.data;
};

export const getObjectHistory = async (authToken, objectType, objectId) => {
  const response = await api.get(`${API_URL}${objectType}s/${objectId}/history/`, {
    headers: { 'Authorization': `Token ${authToken}`, }
  });
  console.log(response)
  return response.data;
};

// Lead Appointments
export const getLeadAppointments = async (authToken, leadId) => {
  try {
    const response = await api.get(`${API_URL}leads/${leadId}/appointments/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching lead appointments:', error);
    throw error;
  }
};

export const getLeadAppointment = async (authToken, leadId, appointmentId) => {
  if (!appointmentId) {
    throw new Error('Appointment ID is required');
  }
  try {
    const response = await api.get(`${API_URL}leads/${leadId}/appointments/${appointmentId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching lead appointment:', error);
    throw error;
  }
};

export const createLeadAppointment = async (authToken, leadId, appointmentData) => {
  try {
    const response = await api.post(`${API_URL}leads/${leadId}/appointments/`, appointmentData, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating lead appointment:', error);
    throw error;
  }
};

export const updateLeadAppointment = async (authToken, leadId, appointmentId, data) => {
  try {
    const response = await api.put(`${API_URL}leads/${leadId}/appointments/${appointmentId}/`, data, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lead appointment:', error);
    throw error;
  }
};

export const deleteLeadAppointment = async (authToken, leadId, appointmentId) => {
  try {
    await api.delete(`${API_URL}leads/${leadId}/appointments/${appointmentId}/`, {
      headers: { Authorization: `Token ${authToken}` }
    });
  } catch (error) {
    console.error('Error deleting lead appointment:', error);
    throw error;
  }
};

export const sendLeadAppointmentReminder = async (authToken, leadId, appointmentId) => {
  try {
    const response = await api.post(`${API_URL}leads/${leadId}/appointments/${appointmentId}/send_reminder/`, {}, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending appointment reminder:', error);
    throw error;
  }
};

export const sendLeadAppointmentConfirmation = async (authToken, leadId, appointmentId) => {
  try {
    const response = await api.post(`${API_URL}leads/${leadId}/appointments/${appointmentId}/send_confirmation/`, {}, {
      headers: { Authorization: `Token ${authToken}` }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending appointment confirmation:', error);
    throw error;
  }
};

// Service-related API functions
export const getCompanyServices = async (authToken) => {
  // The axios interceptor will automatically add the Bearer token
  const response = await api.get(`${API_URL}company-services/`);
  return response.data;
};

export const createCompanyService = async (authToken, serviceData) => {
  // The axios interceptor will automatically add the Bearer token
  const response = await api.post(`${API_URL}company-services/`, serviceData);
  return response.data;
};

export const updateCompanyService = async (authToken, serviceId, serviceData) => {
  // The axios interceptor will automatically add the Bearer token
  const response = await api.put(`${API_URL}company-services/${serviceId}/`, serviceData);
  return response.data;
};

export const deleteCompanyService = async (authToken, serviceId) => {
  // The axios interceptor will automatically add the Bearer token
  const response = await api.delete(`${API_URL}company-services/${serviceId}/`);
  return response.data;
};

export const getLeadServices = async (authToken, leadId) => {
  const response = await api.get(`${API_URL}leads/${leadId}/services/`, {
    headers: { 'Authorization': `Token ${authToken}` }
  });
  
  // Handle paginated response from DRF  
  if (response.data && response.data.results && Array.isArray(response.data.results)) {
    return response.data.results;
  } else if (Array.isArray(response.data)) {
    return response.data;
  } else {
    console.error('getLeadServices: Unexpected response format:', response.data);
    return [];
  }
};

export const updateLeadServices = async (authToken, leadId, services) => {
  // First, get existing services
  const existingServices = await getLeadServices(authToken, leadId);
  
  // Delete services that are no longer selected
  const servicesToDelete = existingServices.filter(
    existing => !services.find(s => s.service === existing.service)
  );
  
  for (const service of servicesToDelete) {
    await api.delete(`${API_URL}leads/${leadId}/services/${service.id}/`, {
      headers: { 'Authorization': `Token ${authToken}` }
    });
  }
  
  // Add new services
  const servicesToAdd = services.filter(
    service => !existingServices.find(existing => existing.service === service.service)
  );
  
  for (const service of servicesToAdd) {
    await api.post(`${API_URL}leads/${leadId}/services/`, service, {
      headers: { 'Authorization': `Token ${authToken}` }
    });
  }
  
  // Return updated services
  return await getLeadServices(authToken, leadId);
};
